import React from "react";

import { Dialog } from "@mui/material";
import { useDepositStyles } from "src/components/wallets/Modal/deposit/deposit.styles";
import FbDepositCrypto from "src/components/wallets/Modal/deposit/FbDepositCrypto";
import ModalHeaderV2 from "src/components/wallets/Modal/deposit/ModalHeaderV2";
import NonFbDepositCryptoModal from "src/components/wallets/Modal/deposit/NonFbDepositCryptoModal";
import LoadingOrErrorManager from "src/components/wallets/Modal/LoadingOrErrorManager";
import ModalTransition from "src/components/wallets/Modal/ModalTransition";
import {
  ProcessDepositApiArg,
  useProcessDepositMutation,
  useAcceptDepositDisclosureMutation,
} from "src/store/features/api/generated";
import { DepositModaleState, setTransactionData } from "src/store/features/depositModal.slice";
import { RootState, useAppDispatch, useAppSelector } from "src/store/store";
import { getSessionItem, setSessionCookie } from "src/utils/SessionManagement";
import { TransactionDisclosureModal } from "src/components/disclosures/TransactionDisclosureModal";
import useTransactionDisclosure from "src/utils/hooks/useTransactionDisclosure";
import { useDepositDisclosure } from "src/utils/hooks/useLegalDisclosure";
import {
  useUserSlice,
  setUserDepositDisclosures,
  type DepositDisclosureData,
} from "src/store/features/user.slice";
import { toFriendlyRestrictedMessage } from "src/utils/restrictedCountry";

interface DepositCryptoModalProps {
  isOpen: boolean;
  onClose: () => void;
  isFbAsset: boolean;
  token: {
    logoUrl?: string;
    name: string;
    depositable?: boolean;
    address?: string;
    code: string;
    format: string;
    memo?: string;
    id: string;
  };
  isLoading: boolean;
  error: any;
}

const SUCCESS_MESSAGE =
  "Thanks, you've successfully submitted your deposit. This deposit will soon appear on your transactions page.";

const REPEAT_TRANSACTION_MESSAGE =
  "This transaction has already been created, check your transactions page";

const SESSION_KEY = "trx";

const MINIMUM_DEPOSIT = ["DOT"];

// an object of the 30 most deposited currencies and an example TXID
const EXAMPLE_TXID: Record<string, string> = {
  "erc-20": "0x4fe2b54328c5bd06d57873815a23050d56c2ccc9a45014f3384b438e1c9cd6ff",
  theta: "0x4fe2b54328c5bd06d57873815a23050d56c2ccc9a45014f3384b438e1c9cd6ff",
  stellar: "5dc4496e9adc82fd6790b0811f4a3e3597be6307a16873869bfa22874db492f5",
  bitcoin: "bc19db6d3626ffb406cbe96c896c7ceb0c65fbac11900d0a08b2925e23fc1734",
  xrp: "F84EC727BC636D3F63F36D2A7F475934DA57BE2B40675C9D251030DE60721BEF",
  cardano: "aa5ab6cf7493e0d14a2e1b54f92ef510618ed47bb61f8c7d5f70e96b33767e46",
  litecoin: "bc19db6d3626ffb406cbe96c896c7ceb0c65fbac11900d0a08b2925e23fc1734",
  n3: "fdacd8d43da605752e450ac7af5a8502d5c7b258a1544282415980747858c837",
  "bitcoin cash": "ffe44b930aa7c9135010bc70d1b94fc73e45e96b718ac03440be9d4fac2ef591",
  digibyte: "36227732e186d3b3f9b1775bb30e936166dc8aea09901de5cf27ded9aadc0c70",
  "hedera hashgraph": "0.0.407221@1668476827.320974791",
  zilliqa: "0xf64c2c7d93cb4a2a7f58049fa6286e9b4d43cf7a23d382a556c6444e353b5deb",
  vechain: "0x9061d6b81da2751717d65b2ac73259afec5c525eb414cdf3ab122bba54e17f7f",
  doge: "90309169a02a091e9427c379b405c07a47c4b38fc1c2be9fd38e71cfd6ffe8f6",
  polkadot: "0x2fb7a9c9e87bb24f05964c0f896d703482b2b2b944be97d7fece5f9df740b014",
  tron: "e2e76cd3d8985487ea3aeb1892e0b4e4afcf4a3abbffb79442645951ee8e2a7c",
  "binance smart chain (bep20)":
    "0xd46ff14fd91dbd8ca892c8147ff897e749bc0e8abf813db615b886f42ccbace8",
  nem: "3f056a54482c7ab118d440a713c6bcbcdd926674c8faec9ab6db5d505de66bce",
  etc: "0x5a9047a611d8080e440bf80669726b7b347cfa4774fd9da6ce1c0de91cde6181",
  algorand: "MYD3JH2B37PG4FDGQED5HDOOCM63CEBIKF7GR4FWW7O6CZWS2CKA",
  tezos: "ooh9vEKFvjdYZYvNZ646s7ebxkBrPvLmJtomuf4ZLFx9T3qdshf",
  qtum: "4242d03e59882203a7a999516bbaf77f37e370b7246d0a7f802467eb46f9c871",
  solana:
    "3cu2NtfVCiMna5uDNV9kw71hLLb9gieapcgLjhjp3ypxQSckqgeT9zgf2xtXL2rD2kNhGp7MxYES2prAjCaCULqo",
  cosmos: "73CD6F01F455ABD0AF43ACD0C2DDC236B3084DD76C300C9A77F593D9D9BC5F60",
  "worldwide asset exchange": "a0b2ef9bde5c5bec747d52e6cc06b12d9571d58d287e2aa0ae4982c682c6790b",
  eos: "507f25228553361d5ac14f5927c5a7e6de746aae5d026c73c254813cbabe1330",
  iost: "5pmmS8BbFBtc7ahtmUxSwF2BUNiwfWU3S7gVJwe8rmPJ",
  "xdc mainnet": "0x6d3e7ff5330f19875030e8db58fce0b3ba4f13ac6f662d925c2008be6ac783ed",
  reddcoin: "b024ee18a52ce627045088128c234ead705726c2c5e17a7a3cf0dcd1b1728598",
  "internet computer": "442eaff7a501dfc026e676b08d76504c0d2d0c5d97926f967b05f72f0f30a24f",
};

const DepositCryptoModal = (props: DepositCryptoModalProps): JSX.Element => {
  const { isOpen, onClose, isFbAsset, token, isLoading, error } = props;

  const dispatch = useAppDispatch();
  const classes = useDepositStyles();

  // ================= Redux State =================
  const { transactionData } = useAppSelector(
    (state: RootState): DepositModaleState => state.depositModal,
  );
  const { depositDisclosures, address } = useUserSlice();

  // ================= Component variables =================
  const [showDepositAddresses, setShowDepositAddresses] = React.useState<boolean>(false);

  const [depositErrorMsg, setDepositErrorMsg] = React.useState<string>("");
  const [pendingDepositData, setPendingDepositData] = React.useState<{
    event: any;
    txId: string;
    amount: string;
  } | null>(null);
  const [showDisclosureModal, setShowDisclosureModal] = React.useState<boolean>(false);

  // ================= Deposit disclosure acceptance state =================
  const [acceptDepositDisclosure] = useAcceptDepositDisclosureMutation();
  const { note: depositNote, version: depositVersion } = useDepositDisclosure();

  const hasAcceptedDepositDisclosure = React.useMemo(() => {
    return depositDisclosures.some(
      (d) =>
        d.type === "Crypto" &&
        d.accepted &&
        (depositVersion == null || d.disclosureVersion === depositVersion),
    );
  }, [depositDisclosures, depositVersion]);

  // ================= Transaction-specific disclosure =================
  const {
    transactionDisclosure,
    isLoading: isDisclosureLoading,
    modalProps,
    closeModal: handleDisclosureClose,
  } = useTransactionDisclosure((disclosureId) => {
    // Disclosure accepted — determine what to do based on deposit type
    const userState = address?.state?.trim().toLowerCase() ?? "";

    // Save deposit disclosure acceptance to backend (append-only; backend keeps full history)
    acceptDepositDisclosure({
      acceptDepositDisclosureDto: {
        state: userState,
        isAccepted: true,
        type: "Crypto",
        ...(depositVersion != null && {
          disclosureVersion: depositVersion,
        }),
      },
    })
      .unwrap()
      .then(() => {
        // Optimistically append new acceptance so Redux matches backend's append-only list
        const now = Date.now();
        const newEntry: DepositDisclosureData = {
          state: userState,
          accepted: true,
          timestamp: now,
          type: "Crypto",
          ...(depositVersion != null && {
            disclosureVersion: depositVersion,
          }),
        };
        dispatch(setUserDepositDisclosures([...depositDisclosures, newEntry]));
      })
      .catch((err) => {
        console.error("Failed to save deposit disclosure acceptance", err);
      });

    // For non-FB deposits, proceed with the pending deposit submission
    if (!isFbAsset && pendingDepositData) {
      processDeposit(pendingDepositData.event, pendingDepositData.txId, pendingDepositData.amount);
      setPendingDepositData(null);
    }

    setShowDisclosureModal(false);
  });

  // Override the modal open state. Only close the deposit modal when user dismisses (backdrop/escape), not when they accept.
  const disclosureModalProps = React.useMemo(
    () => ({
      ...modalProps,
      open: showDisclosureModal && !!transactionDisclosure,
      noticeTextOverride: depositNote,
      onClose: (_event?: any, reason?: string) => {
        setShowDisclosureModal(false);
        setPendingDepositData(null);
        handleDisclosureClose();
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          onClose(); // Close deposit modal so user is back at wallet dropdown
        }
      },
    }),
    [modalProps, showDisclosureModal, transactionDisclosure, depositNote, handleDisclosureClose, onClose],
  );

  // ================= Deposit disclosure on modal open (same pattern as BankingBreakdown) =================
  // Show disclosure once when the crypto deposit modal is shown, if required and not yet accepted (FB and non-FB)
  const depositDisclosureShownRef = React.useRef(false);
  React.useEffect(() => {
    if (
      isOpen &&
      transactionDisclosure &&
      !hasAcceptedDepositDisclosure &&
      !depositDisclosureShownRef.current
    ) {
      depositDisclosureShownRef.current = true;
      setShowDisclosureModal(true);
    }
    // Reset the ref when the modal is closed so it triggers again on next open
    if (!isOpen) {
      depositDisclosureShownRef.current = false;
    }
  }, [isOpen, transactionDisclosure, hasAcceptedDepositDisclosure]);

  // ================= Functions / Handlers =================
  const [postDeposit, depositRes] = useProcessDepositMutation();

  // onClick show qr code and deposit address component
  const handleShowDepositAddress = (): void => {
    setShowDepositAddresses(true);
  };

  // close modal and reset variables and rtk query response
  const handleClosingModal = (): void => {
    onClose();
    // setTimeout is used to avoid visual bug when closing modal, to avoid a FbDepositCrypto component from renderring when closing
    setTimeout((): void => {
      setDepositErrorMsg("");
      setShowDepositAddresses(false);
      depositRes.reset();
    }, 300);
  };

  // Process deposit after disclosure is accepted (non-FB only)
  const processDeposit = async (event: any, txId: string, amount: string): Promise<void> => {
    event.preventDefault();

    let zurichid: string = txId + amount;
    if (getSessionItem(SESSION_KEY) === zurichid) {
      setDepositErrorMsg(REPEAT_TRANSACTION_MESSAGE);
      return;
    }
    setSessionCookie(SESSION_KEY, zurichid); // set session storage to avoid user entering a duplicate deposit

    const body: ProcessDepositApiArg = {
      processDepositDto: {
        token: transactionData.code, // e.g. BTC
        amount: Number(amount), // e.g. 0.4
        tx_id: txId, // e.g. bc1q6rjsk9223a6qcpee2mhsyw729gv6m66lp5qtuj
        address_id: transactionData.id, // e.g. 577
        zurich_id: zurichid, // e.g. bc1q6rjsk9223a6qcpee2mhsyw729gv6m66lp5qtuj0.4
      },
    };

    try {
      await postDeposit(body).unwrap();
    } catch (e) {
      const err = e as any;
      const rawMessage = err?.data?.message ?? "Error Occured";
      // Block depositing a token restricted for the user's country with a friendly message
      setDepositErrorMsg(toFriendlyRestrictedMessage(rawMessage) ?? rawMessage);
    }
  };

  // handle the submitting of form callback (non-FB deposits only)
  const handleSubmit = async (event: any, txId: string, amount: string): Promise<void> => {
    // Always prevent default FIRST to stop form submission and page refresh
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }

    // Don't proceed while the disclosure is still loading — a null transactionDisclosure
    // here means "unknown", not "not required". The on-open effect shows the modal once it resolves.
    if (isDisclosureLoading) {
      return;
    }

    // Check if disclosure is required and not yet accepted today
    if (transactionDisclosure && !hasAcceptedDepositDisclosure && !showDisclosureModal) {
      // Store the deposit data and show disclosure modal
      setPendingDepositData({ event, txId, amount });
      setShowDisclosureModal(true);
      return;
    }

    // If disclosure modal is already open, don't proceed
    if (showDisclosureModal) {
      return;
    }

    // Disclosure not required or already accepted today — proceed directly
    await processDeposit(event, txId, amount);
  };

  // ================= React UseEffect =================
  React.useEffect((): void => {
    // Get/Set saved token code and address
    if (token.code && token.address) {
      dispatch(setTransactionData({ code: token.code, id: token.id }));
    }
  }, [token]);

  React.useEffect((): void => {
    // if error coming from props set it then render it
    if (error) {
      const rawMessage = typeof error === "string" ? error : error?.data?.message ?? error;
      setDepositErrorMsg(toFriendlyRestrictedMessage(rawMessage) ?? rawMessage);
    }
  }, [error]);

  return (
    <Dialog
      open={isOpen}
      TransitionComponent={ModalTransition}
      onClose={handleClosingModal}
      aria-describedby="deposit-crypto-modal"
      PaperProps={{ className: classes.paperProps }}
      // hideBackdrop
    >
      <LoadingOrErrorManager
        error={{
          onClose: handleClosingModal,
          message: depositErrorMsg || error,
          isError: depositRes.isError || Boolean(depositErrorMsg),
        }}
        success={{
          onClose: handleClosingModal,
          message: SUCCESS_MESSAGE,
          isSuccess: depositRes.isSuccess,
        }}
        loading={isLoading || depositRes.isLoading}>
        <>
          <ModalHeaderV2 title={token.name} onClose={handleClosingModal} />

          <div style={{ padding: "16px 20px" }} data-testid="deposit-modal">
            {isFbAsset ? (
              <FbDepositCrypto
                isFbAsset={isFbAsset}
                minimumDeposit={{
                  hasMinimum: MINIMUM_DEPOSIT.includes(token.code),
                  message: "More than 1.05 DOT",
                }}
                depositAddress={token.address ?? ""}
                depositNetwork={token.format ?? ""}
                depositMemo={token.memo ?? ""}
                showDepositAddresses={showDepositAddresses}
                onShowDepositAddressChange={handleShowDepositAddress}
                tokenCode={token.code ?? ""}
              />
            ) : (
              // use the FbDepositCrypto component for the top portion of modal
              <>
                <FbDepositCrypto
                  isFbAsset={isFbAsset}
                  depositAddress={token.address ?? ""}
                  depositNetwork={token.format ?? ""}
                  depositMemo={token.memo ?? ""}
                  showDepositAddresses={showDepositAddresses}
                  onShowDepositAddressChange={handleShowDepositAddress}
                  tokenCode={token.code ?? ""}
                />
                <NonFbDepositCryptoModal
                  showDepositAddresses={showDepositAddresses}
                  exampleTransactionID={EXAMPLE_TXID[token.format.toLowerCase()] ?? ""}
                  onSubmit={handleSubmit}
                />
              </>
            )}

          </div>
        </>
      </LoadingOrErrorManager>

      {/* TRANSACTION-SPECIFIC DISCLOSURE MODAL */}
      {transactionDisclosure && <TransactionDisclosureModal {...disclosureModalProps} />}
    </Dialog>
  );
};

export default DepositCryptoModal;
